import { compareNatural, toMatchKey } from "./text";
import {
  OBJECTIVE_NONE_CODE,
  UNCLASSIFIED_CODE,
  type Intervention,
  type ObjectiveValue,
  type View,
} from "./types";

/**
 * Stored chip selections keyed by selection key (`objectives` or
 * `view:<id>`). A missing entry means the user has not touched that chip
 * group yet, so its default selection applies.
 */
export type ChipSelections = Readonly<Record<string, ReadonlySet<string>>>;

export interface ChipEntry {
  label: string;
  codes: string[];
}

export function getChipSelectionKey(view: View): string | null {
  if (view.chipDisplay === "objectives") return "objectives";
  if (view.chipDisplay === "view") return `view:${view.id}`;
  return null;
}

/** View value codes in display order, with "unclassified" last if present. */
export function getOrderedViewChipCodes(
  view: View,
  available: ReadonlySet<string> | undefined,
): string[] {
  const codes = view.order.filter((code) => available?.has(code));
  if (available?.has(UNCLASSIFIED_CODE)) codes.push(UNCLASSIFIED_CODE);
  return codes;
}

/**
 * Default selection for an untouched chip group: everything. For readers
 * (`showAll` false) the UNODC evidence-level view (F) hides "not prevention"
 * (8), "duplicate" (9) and unclassified items.
 */
export function getDefaultViewSelection(
  view: View,
  available: ReadonlySet<string> | undefined,
  showAll = false,
): Set<string> {
  const codes = getOrderedViewChipCodes(view, available);
  if (showAll || view.id !== "F") return new Set(codes);
  return new Set(
    codes.filter((code) => code !== UNCLASSIFIED_CODE && code !== "8" && code !== "9"),
  );
}

export interface FilterContext {
  view: View;
  availableViewCodes: Record<string, Set<string>>;
  /** Objective codes present in the dataset. */
  availableObjectives: ReadonlySet<string>;
  chipSelections: ChipSelections;
  /** Statistics tab with the filter row collapsed applies only view defaults. */
  statsFiltersHidden: boolean;
  /** Untouched chip groups select every value (all roles except readers). */
  showAllByDefault: boolean;
}

export function getEffectiveViewSelection(context: FilterContext): ReadonlySet<string> {
  const { view, availableViewCodes, chipSelections } = context;
  return (
    chipSelections[`view:${view.id}`] ??
    getDefaultViewSelection(view, availableViewCodes[view.id], context.showAllByDefault)
  );
}

export function getEffectiveObjectiveSelection(context: FilterContext): ReadonlySet<string> {
  return context.chipSelections.objectives ?? context.availableObjectives;
}

function matchesViewChips(item: Intervention, context: FilterContext): boolean {
  const { view, availableViewCodes } = context;
  const available = availableViewCodes[view.id];
  if (!available?.size) return true;
  const selected = getEffectiveViewSelection(context);
  if (!selected.size) return false;
  const code = item.codes[view.id];
  return selected.has(code && view.values[code] ? code : UNCLASSIFIED_CODE);
}

function matchesObjectiveChips(item: Intervention, context: FilterContext): boolean {
  if (!context.availableObjectives.size) return true;
  const selected = getEffectiveObjectiveSelection(context);
  return item.objectiveCodes.some((code) => selected.has(code));
}

/** Applies the chip filter of the selected view to one intervention. */
export function matchesChipFilter(item: Intervention, context: FilterContext): boolean {
  const mode = context.view.chipDisplay;
  if (context.statsFiltersHidden) {
    if (mode !== "view") return true;
    return getEffectiveViewSelection(context).size ? matchesViewChips(item, context) : true;
  }
  if (mode === "objectives") return matchesObjectiveChips(item, context);
  if (mode === "view") return matchesViewChips(item, context);
  return true;
}

/** Whether unclassified items are currently shown for the selected view. */
export function isUnclassifiedVisible(context: FilterContext): boolean {
  if (!context.availableViewCodes[context.view.id]?.has(UNCLASSIFIED_CODE)) return true;
  return getEffectiveViewSelection(context).has(UNCLASSIFIED_CODE);
}

export function matchesRegion(item: Intervention, selectedRegions: ReadonlySet<string>): boolean {
  return selectedRegions.size === 0 || selectedRegions.has(item.region);
}

/** Case-insensitive search over the item number and both titles. */
export function matchesSearch(item: Intervention, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return [item.itemNumber, item.display.no, item.display.en]
    .join(" ")
    .toLowerCase()
    .includes(needle);
}

/**
 * Chip entries for the objectives filter. Objectives with the same
 * (translated) label are grouped into one chip; "none" is only offered when
 * an item in the selected regions actually has no objective.
 */
export function getObjectiveChipEntries(
  objectives: Record<string, ObjectiveValue>,
  available: ReadonlySet<string>,
  items: Intervention[],
  selectedRegions: ReadonlySet<string>,
  translate: (value: ObjectiveValue, code: string) => string,
): ChipEntry[] {
  const groups = new Map<string, ChipEntry>();
  const hasNone = items.some(
    (item) =>
      matchesRegion(item, selectedRegions) && item.objectiveCodes.includes(OBJECTIVE_NONE_CODE),
  );
  [...available]
    .filter((code) => objectives[code] && (code !== OBJECTIVE_NONE_CODE || hasNone))
    .sort(compareNatural)
    .forEach((code) => {
      const label = translate(objectives[code]!, code) || code;
      const key = toMatchKey(label) || code;
      const group = groups.get(key) ?? { label, codes: [] };
      group.codes.push(code);
      groups.set(key, group);
    });
  return [...groups.values()];
}
